package uk.co.nickthecoder.kyd

import uk.co.nickthecoder.glok.property.boilerplate.booleanProperty
import uk.co.nickthecoder.glok.property.boilerplate.colorProperty
import uk.co.nickthecoder.glok.property.boilerplate.stringProperty
import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.kyd.appearance.Appearance
import uk.co.nickthecoder.kyd.appearance.AppearanceResource
import uk.co.nickthecoder.kyd.appearance.NoAppearance

/**
 * [Pick]s appear in a side panel of the editor, and can be `picked` and dropped onto a
 * [Stage] when editing an [Act].
 *
 * Each [Pick] is a prototype for an [Actor].
 * [Pick]s are grouped using [PickGroup]
 *
 * Note. [Pick] should be considered an internal implementation used only by the Kyd editor.
 * They are defined in subproject kyd-core (and not kyd-editor) because they are part of the `.kyd`
 * file-format. But that part of the file-format isn't used during game-play, only by the editor.
 */
class Pick(name: String = "") {

    val nameProperty by stringProperty(name)
    var name by nameProperty

    val resourcePathProperty by stringProperty("")
    var resourcePath by resourcePathProperty

    val tintProperty by colorProperty(Color.WHITE)
    var tint by tintProperty

    val showLabelProperty by booleanProperty(false)
    var showLabel by showLabelProperty


    fun plainAppearance(game: Game): Appearance? {
        val res = game.find(resourcePath) ?: return NoAppearance
        if (res is Appearance) return res
        if (res is AppearanceResource) return res.appearance()

        return null
    }

    fun resource(game: Game): Resource? = game.find(resourcePath)

    fun role(game: Game): Role? = resource(game) as? Role

    fun createActor(game: Game, stage: Stage): Actor {
        val resource = resource(game) ?: throw IllegalStateException("Resource '$resourcePath' not found")

        if (resource is Role) {
            return resource.createActor(stage)
        }

        return Actor(stage).also {
            tint = this@Pick.tint
            it.role = resource as? Role
            it.appearance = (resource as? AppearanceResource)?.appearance() ?: NoAppearance
        }
    }

    fun copy() = Pick(name).also {
        it.resourcePath = resourcePath
        it.tint = tint
    }
}
